import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Briefcase, IndianRupee, Layers } from "lucide-react";

// --- Helper Components ---

// StarRating Helper
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300" 
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <svg
      className="w-3 h-3 mr-1 text-red-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// --- Tools Section (Fixed: Clearly Shows Choice) ---
const ToolsYouWillMaster = () => {
  // 1. Common Tools
  const commonTools = [
    { name: 'Angular', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angular/angular-original.svg' },
    { name: 'TypeScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg' },
    { name: 'RxJS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/rxjs/rxjs-original.svg' },
    { name: 'Bootstrap', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/bootstrap/bootstrap-original.svg' },
    { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg' },
    { name: 'Postman', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postman/postman-original.svg' },
  ];

  // 2. Option A: Node.js Tools
  const nodeTools = [
    { name: 'Node.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg' },
    { name: 'MongoDB', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg' },
    { name: 'Express', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/express/express-original.svg' },
  ];

  // 3. Option B: Java Tools
  const javaTools = [
    { name: 'Spring Boot', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/spring/spring-original.svg' },
    { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original-wordmark.svg' },
    { name: 'Hibernate', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/hibernate/hibernate-original.svg' },
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 text-center">
        
        {/* Heading */}
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-black font-aligran">
          Full Stack Technologies You Will <span className="text-red-600">Master</span>
        </h2>
        <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
          We start with a strong Frontend web development foundation, then you <b>choose your backend specialization</b>.
        </p>

        {/* 1. CORE FRONTEND TOOLS */}
        <div className="mb-12">
          <h3 className="text-lg font-bold text-gray-500 uppercase tracking-widest mb-6">Core Frontend Fundamentals</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {commonTools.map((tool, i) => (
              <motion.div
                key={i}
                className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all w-28 h-28"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <img src={tool.logo} alt={`Learn ${tool.name} for Frontend Development`} className="w-10 h-10 mb-2 object-contain" />
                <p className="text-xs font-semibold text-gray-800">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* DIVIDER - CHOOSE YOUR PATH */}
        <div className="relative flex items-center justify-center mb-12">
          <div className="border-t border-gray-300 w-full max-w-xs absolute"></div>
          <span className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-bold z-10 shadow-lg tracking-wide">
            CHOOSE YOUR BACKEND PATH
          </span>
        </div>

        {/* 2. BACKEND SELECTION */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          
          {/* OPTION A: MEAN STACK */}
          <div className="bg-white p-8 rounded-2xl border-2 border-dashed border-gray-200 hover:border-green-500 transition-colors relative group">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-100 text-green-700 px-4 py-1 rounded-full text-xs font-bold border border-green-200">
              OPTION A: MEAN STACK
            </div>
            <h4 className="text-xl font-bold mb-6 text-gray-800">Node.js Developer Path</h4>
            <div className="flex justify-center gap-4">
              {nodeTools.map((tool, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center mb-2 group-hover:bg-green-50 transition">
                    <img src={tool.logo} alt={`${tool.name} for MEAN Stack Development`} className="w-8 h-8 object-contain" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{tool.name}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">Best for: Startups, Real-time Web Apps & Full Stack JS Developers.</p>
          </div>

          {/* OR Circle for Desktop */}
          <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 mt-20 items-center justify-center w-10 h-10 bg-gray-200 rounded-full text-gray-500 font-bold text-xs border-4 border-gray-50 z-10">
            OR
          </div>

          {/* OPTION B: JAVA FULL STACK */}
          <div className="bg-white p-8 rounded-2xl border-2 border-dashed border-gray-200 hover:border-red-500 transition-colors relative group">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-100 text-red-700 px-4 py-1 rounded-full text-xs font-bold border border-red-200">
              OPTION B: JAVA STACK
            </div>
            <h4 className="text-xl font-bold mb-6 text-gray-800">Spring Boot Java Path</h4>
            <div className="flex justify-center gap-4">
              {javaTools.map((tool, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center mb-2 group-hover:bg-red-50 transition">
                    <img src={tool.logo} alt={`${tool.name} for Java Full Stack Development`} className="w-8 h-8 object-contain" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{tool.name}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">Best for: Banking, Large MNCs & Secure Enterprise Systems.</p>
          </div>

        </div>

      </div>
    </section>
  );
};

const AngularFullStack = () => {
  // --- Data ---
  const testimonials = [
    {
      id: 1,
      name: "Sandeep Malhotra",
      review:
        "I wanted to switch from Java legacy to Modern Full Stack. The combination of Angular with Spring Boot was exactly what I needed. Great curriculum for Java developers!",
      rating: 5,
      date: "2024-08-01",
      profileImg: "https://randomuser.me/api/portraits/men/11.jpg", 
      source: "LinkedIn",
    },
    {
      id: 2,
      name: "Priya Desai",
      review:
        "The RxJS module and Spring Boot integration were the highlights. I learned how to connect an Angular frontend with a robust Java backend seamlessly.",
      rating: 5,
      date: "2024-07-29",
      profileImg: "https://randomuser.me/api/portraits/women/33.jpg", 
      source: "Google Reviews",
    },
    {
      id: 3,
      name: "Amitabh Roy",
      review:
        "I chose the MEAN stack path (Node.js). The project-based approach helped me build a complete full stack inventory management system from scratch.",
      rating: 4,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/22.jpg", 
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Kavita Singh",
      review:
        "Angular's complexity was made easy by the mentors. Best place to learn Enterprise Web Application Development.",
      rating: 5,
      date: "2024-07-20",
      profileImg: "https://randomuser.me/api/portraits/women/29.jpg",
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Rahul Verma",
      review:
        "Perfect full stack training. I learned both Node.js and basic Spring Boot API connectivity. It gave me a lot of flexibility for tech interviews.",
      rating: 4,
      date: "2024-07-15",
      profileImg: "https://randomuser.me/api/portraits/men/44.jpg",
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "What backend framework will I learn (Node.js or Spring Boot)?",
      answer:
        "We offer flexibility in our full stack development training. You can choose the MEAN stack path (Node.js/Express) or the Java Full Stack path (Spring Boot). Both connect seamlessly with the Angular frontend.",
    },
    {
      title: "Do I need prior Java knowledge for the Spring Boot track?",
      answer:
        "For the Spring Boot module, basic Core Java programming knowledge is helpful. However, we cover the essential Java concepts required to build scalable REST APIs.",
    },
    {
      title: "Why is combining Angular with Java Spring Boot popular?",
      answer:
        "This technology stack combination is the industry standard for large-scale, highly secure enterprise web applications (like Banking, Insurance, and E-commerce platforms), ensuring high job employability.",
    },
    {
      title: "How does Angular development differ from React?",
      answer:
        "Angular is a comprehensive web framework with built-in tools (Routing, HTTP modules), whereas React is a UI library. Angular is heavily preferred for enterprise-level applications, strict TypeScript structure, and large development teams.",
    },
    {
      title: "What full stack projects will I build during the course?",
      answer:
        "You will build an Enterprise CRM or Banking Application using an Angular frontend paired with your chosen backend database and API (Node.js/MongoDB or Spring Boot/MySQL).",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: TypeScript Fundamentals for Angular",
      description: "Learn Static typing, Interfaces, Classes, Generics, and Decorators - the essential foundation for modern Angular development.",
    },
    {
      title: "Module 2: Angular Framework Architecture",
      description: "Understanding Modules (NgModules), Components, Templates, modern Standalone Components, and mastering the Angular CLI.",
    },
    {
      title: "Module 3: Data Binding & Angular Directives",
      description: "Explore One-way vs Two-way data binding, Structural directives (*ngIf, *ngFor), and reactive Signals (New in Angular 18).",
    },
    {
      title: "Module 4: Services & Dependency Injection (DI)",
      description: "Creating injectable software services, managing application state across components, and utilizing Singleton design patterns.",
    },
    {
      title: "Module 5: RxJS & Asynchronous Observables",
      description: "Mastering asynchronous JavaScript programming using RxJS, Observables, Subjects, and map/filter operators.",
    },
    {
      title: "Module 6: SPA Routing & Navigation",
      description: "Implementing Single Page Application (SPA) routing, creating Child routes, securing routes with Guards (AuthGuard), and Lazy Loading for performance.",
    },
    {
      title: "Module 7: Angular Forms & Validation",
      description: "Deep dive into complex Template-Driven Forms and secure Reactive Forms with custom backend validators.",
    },
    {
      title: "Module 8: Backend API Integration (Node.js OR Spring Boot)",
      description: "Connecting your Angular UI to real APIs. Choose your tech stack: Build scalable REST endpoints using Express.js (Node backend) OR Spring Boot (Java backend).",
    },
    {
      title: "Module 9: Web App Deployment & Testing",
      description: "Performing Unit testing with Jasmine/Karma, generating Production-ready builds, Docker containerization basics, and Cloud server deployment.",
    },
  ];

  // --- Logic for Carousel ---
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(autoScrollInterval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(currentTestimonialIndex + i) % testimonials.length]
    );
  }

  // --- Logic for Form ---
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! We will contact you shortly regarding our Full Stack training program.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>Best Angular Full Stack Course | Learn Node.js & Java Spring Boot</title>
        <meta name="description" content="Master Angular full-stack web development. Choose between the MEAN stack (Node.js) or Java Full Stack (Spring Boot) path to build enterprise applications." />
        <meta name="keywords" content="Angular full stack course, Java full stack development, MEAN stack training, Learn Angular and Spring Boot, Node.js backend, MERN stack alternative, Enterprise web development" />
        <meta property="og:title" content="Angular Full Stack Web Development Mastery" />
        <meta property="og:description" content="Become an expert in the Angular framework with a Node.js or Java Spring Boot backend. Industry-standard curriculum." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="relative overflow-hidden h-[550px] flex items-center bg-black"> 
      
      {/* Background Image + Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=2070&auto=format&fit=crop"
          alt="Angular Full Stack Web Development Training Background"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-red-900/40" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 py-1 mb-4 border border-red-500 rounded-full bg-red-900/30 text-red-400 text-sm font-semibold tracking-wide">
            ENTERPRISE FULL STACK TRAINING
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 text-gray-100 font-aligran">
            Angular Full Stack Course
            <span className="text-red-600 block">Master Node.js or Spring Boot</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            Learn Google's modern Angular framework. Pair it with <strong>Node.js</strong> for a complete JavaScript stack or <strong>Java Spring Boot</strong> for powerful enterprise backend solutions.
          </p>

          <button
            onClick={() =>
              document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-10 rounded-lg shadow-[0_5px_20px_rgba(220,38,38,0.3)] transition duration-300 transform hover:scale-105"
          >
            Start Learning Full Stack Now
          </button>
        </motion.div>

        {/* Right Side Image */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex justify-center md:justify-end"
        >
          <img
            src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angular/angular-original.svg" 
            alt="Angular Framework Logo for Full Stack Course"
            className="w-72 md:w-80 drop-shadow-[0_5px_30px_rgba(221,0,49,0.4)] transition-transform duration-300 hover:scale-105"
          />
        </motion.div>
      </div>
      </section>

      {/* --- WHY LEARN SECTION --- */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 font-aligran">
            Why Learn <span className="text-red-600">Angular Full Stack?</span>
          </h2>

          <p className="text-gray-600 mb-14 max-w-3xl mx-auto text-lg leading-relaxed">
            Angular developers with backend integration skills (Node.js or Java) are currently the highest-paid software engineers in the startup and corporate IT sectors.
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Briefcase size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Dual Career Opportunity</h3>
              <p className="text-gray-600 leading-relaxed">
                Unlock high-paying jobs in both the MEAN stack (Startups) and Java Full Stack (MNCs/Banking) software domains.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <IndianRupee size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Enterprise Standard</h3>
              <p className="text-gray-600 leading-relaxed">
                The Angular + Spring Boot combination is the "Gold Standard" for building secure, scalable web applications.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Layers size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Strict Code Structure</h3>
              <p className="text-gray-600 leading-relaxed">
                Master industry-standard software engineering practices with TypeScript and Object-Oriented Programming (OOP).
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS SECTION --- */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-red-600">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Live Projects', desc: 'Build real-world CRM & Web Apps with Spring Boot/Node.', img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c' },
              { title: 'Expert Mentors', desc: 'Learn directly from Senior Full Stack Architects.', img: 'https://images.unsplash.com/photo-1531482615713-2afd69097998' },
              { title: 'Certification', desc: 'Industry-recognized developer completion certificate.', img: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f' },
              { title: '100% Placement', desc: 'Mock technical interviews & top MNC referrals.', img: 'https://images.unsplash.com/photo-1603575448878-868a20723f5d' },
              { title: 'Hybrid Learning', desc: 'Join via Online Live classes or Offline Classroom.', img: 'https://cdni.iconscout.com/illustration/premium/thumb/hybrid-learning-8088108-6556132.png' },
              { title: 'Modern Tool Mastery', desc: 'Master RxJS, Spring Boot, Postman API, and MySQL.', img: 'https://static.vecteezy.com/system/resources/previews/018/769/685/original/industrial-revolution-4-0-sci-fi-hologram-and-3d-numbers-with-conceptual-icons-industry-4-0-concept-intelligence-robotics-physical-systems-illustrator-vector.jpg' },
              { title: 'Comprehensive Modules', desc: 'In-depth coverage of both Frontend & Backend code.', img: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d' },
              { title: 'Updated Syllabus', desc: 'Curriculum updated to latest Angular 18+ and Spring Boot 3.', img: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0' },
              { title: '24/7 Support', desc: 'Dedicated code review, mentorship, and doubt solving.', img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-44 border-l-4 border-red-600"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1/2 p-5 flex flex-col justify-center">
                  <h3 className="font-bold text-lg text-black mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={item.img} alt={`${item.title} - Full Stack Training Highlight`} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/40 to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CURRICULUM SECTION --- */}
      <section className="py-20 bg-white"> 
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-left text-gray-900 font-aligran"> 
            Full Stack <span className="text-red-600">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"> 
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-100 hover:bg-gray-200 transition flex justify-between items-center text-gray-900"> 
                    {module.title}
                    <span className="transition-transform group-open:rotate-180 text-gray-900">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-black border-t border-gray-200 leading-relaxed"> 
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TOOLS SECTION --- */}
      <ToolsYouWillMaster />

      {/* --- FAQ & FORM SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10 bg-white"> 
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Web Development FAQs</h2>
          <div className="space-y-4">
            {faqContent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg"> 
                <details className="group p-4">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-gray-700 flex justify-between">
                    {item.title}
                    <span className="text-gray-900 group-open:text-red-600">+</span>
                  </summary>
                  <p className="mt-4 text-black text-sm border-t border-gray-200 pt-3">{item.answer}</p> 
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-red-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-red-600">Book Your Seat</h3>
            <p className="text-center text-gray-600 mb-6 text-sm">Join the next Full Stack Developer batch.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="firstName" placeholder="Your Name" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-red-500 focus:outline-none" value={formData.firstName} onChange={handleInputChange} />
              <input type="email" name="businessEmail" placeholder="Email Address" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-red-500 focus:outline-none" value={formData.businessEmail} onChange={handleInputChange} />
              <input type="tel" name="phone" placeholder="Phone Number" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-red-500 focus:outline-none" value={formData.phone} onChange={handleInputChange} />
              <button type="submit" className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 rounded transition shadow-lg">ENQUIRE FOR TRAINING</button>
              <p className="text-xs text-center text-gray-500 mt-2">We respect your privacy.</p>
            </form>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      <section className="bg-gray-50 py-20 border-t border-gray-200"> 
        <div className="max-w-7xl mx-auto px-6 text-left"> 
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
                Student <span className="text-red-600">Success Stories</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {currentVisibleTestimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col text-left hover:border-red-400/50 transition shadow-sm"
                        >
                            <div className="flex items-center mb-4">
                                <img src={testimonial.profileImg} alt={`${testimonial.name} - Full Stack Course Review`} className="w-12 h-12 rounded-full border-2 border-red-500 mr-3" />
                                <div>
                                    <h4 className="font-bold text-black">{testimonial.name}</h4> 
                                    <span className="text-gray-500 text-xs">{testimonial.source}</span>
                                </div>
                            </div>
                            <div className="mb-3"><StarRating rating={testimonial.rating} /></div>
                            <p className="text-gray-700 text-sm flex-grow">"{testimonial.review}"</p> 
                            <VerifiedBadge />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex justify-start mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        aria-label={`View testimonial ${idx + 1}`}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-red-600 w-6' : 'bg-gray-300'}`}
                    />
                ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default AngularFullStack;